import { useCallback, useMemo, useState, createContext, useRef, useEffect } from "react";
import { AgGrid } from "@jsq/ds/core";
import { type UserRow } from "..";
import { BulkActionsPanel } from "../components/BulkActionsPanel";

export const UserTableContext = createContext<{ users: UserRow[] }>({ users: [] });

export const useSelectRows = ({ gridApi }: { gridApi?: AgGrid.GridApi | null }) => {
  const gridApiRef = useRef<AgGrid.GridApi<UserRow> | null>(null);
  const [selectedRows, setSelectedRows] = useState<UserRow[]>([]);

  useEffect(() => {
    gridApiRef.current = gridApi ?? null;
  }, [gridApi]);

  const onSelectionChanged = useCallback(
    (event: AgGrid.SelectionChangedEvent<UserRow>) => {
      const api = gridApiRef.current;
      const rows = event.api.getSelectedRows();
      setSelectedRows(rows);
      if (rows.length > 0) {
        api?.openToolPanel("bulkActions");
      } else {
        api?.closeToolPanel();
      }
    },
    [gridApiRef],
  );

  const clearSelection = useCallback(() => {
    const api = gridApiRef.current;
    if (api) api.deselectAll();
    setSelectedRows([]);
  }, [gridApiRef]);

  // Default Sidebar Def
  const sideBar: AgGrid.SideBarDef = useMemo(
    () => ({
      position: "right",
      toolPanels: [
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          toolPanelParams: {
            suppressFilterSearch: true,
            suppressColumnFilter: false,
          },
        },
        {
          id: "bulkActions",
          labelDefault: "Bulk Actions",
          labelKey: "bulkActions",
          iconKey: "bulkActions",
          toolPanel: BulkActionsPanel,
          width: 320,
          toolPanelParams: {
            width: 320,
            onClear: clearSelection,
          },
        },
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
          },
        },
      ],
    }),
    [clearSelection],
  );
  return { onSelectionChanged, clearSelection, selectedRows, sideBar };
};
